<template>
    <DForm
        enctype="multipart/form-data"
        @submit.prevent="submitForm"
    >
        <DFormRow
            :label="__('settings.company_information.form.logo')"
            :is-required="companyInformation === null"
            :error="errors.logo"
            :hint="__('settings.company_information.form.logo_hint')"
        >
            <DFileInput
                id="logo"
                accept="image/svg+xml"
                :custom="true"
                :multiple="false"
                :is-valid="isFormFieldValid('logo')"
                :invalid-feedback="errors.logo"
                :placeholder="__('settings.company_information.form.file_choose')"
                @change="addLogo"
            />
            <input
                v-model="form.logo_base64"
                type="hidden"
                name="logo_base64"
            >
            <img
                v-if="logoSrcContents !== null"
                class="company__image"
                :alt="form.name"
                :src="logoSrcContents"
            >
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.logo_dark')"
            :error="errors.logo_dark"
            :hint="__('settings.company_information.form.logo_dark_hint')"
        >
            <DFileInput
                id="logo_dark"
                accept="image/svg+xml"
                :custom="true"
                :multiple="false"
                :is-valid="isFormFieldValid('logo_dark')"
                :invalid-feedback="errors.logo_dark"
                :placeholder="__('settings.company_information.form.file_choose')"
                @change="addLogoDark"
            />
            <input
                v-model="form.logo_dark_base64"
                type="hidden"
                name="logo_dark_base64"
            >
            <img
                v-if="logoDarkSrcContents !== null"
                class="company__image company__image--dark"
                :alt="form.name"
                :src="logoDarkSrcContents"
            >
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.country')"
            :is-required="true"
            :error="errors.country_code"
            :hint="__('settings.company_information.form.country_hint')"
        >
            <DSelect
                id="country_code"
                v-model="form.country_code"
                :options="countries"
                :placeholder="__('settings.company_information.form.country')"
                :is-valid="isFormFieldValid('country_code')"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.default_country')"
            :is-required="true"
            :error="errors.default_country_code"
            :hint="__('settings.company_information.form.default_country_hint')"
        >
            <DSelect
                id="default_country_code"
                v-model="form.default_country_code"
                :options="countries"
                :placeholder="__('settings.company_information.form.default_country')"
                :is-valid="isFormFieldValid('default_country_code')"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.company_name')"
            :is-required="true"
            :error="errors.company_name"
            :hint="__('settings.company_information.form.company_name_hint')"
        >
            <DInput
                id="company_name"
                v-model="form.company_name"
                :placeholder="__('settings.company_information.form.company_name')"
                :is-valid="isFormFieldValid('company_name')"
                maxlength="128"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.address')"
            :is-required="true"
            :error="errors.address"
            :hint="__('settings.company_information.form.address_hint')"
        >
            <DInput
                id="address"
                v-model="form.address"
                :placeholder="__('settings.company_information.form.address')"
                :is-valid="isFormFieldValid('address')"
                maxlength="128"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.post_code')"
            :is-required="true"
            :error="errors.post_code"
            :hint="__('settings.company_information.form.post_code_hint')"
        >
            <DInput
                id="post_code"
                v-model="form.post_code"
                :placeholder="__('settings.company_information.form.post_code')"
                :is-valid="isFormFieldValid('post_code')"
                maxlength="12"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.city')"
            :is-required="true"
            :error="errors.city"
            :hint="__('settings.company_information.form.city_hint')"
        >
            <DInput
                id="city"
                v-model="form.city"
                :placeholder="__('settings.company_information.form.city')"
                :is-valid="isFormFieldValid('city')"
                maxlength="45"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.register_court')"
            :is-required="true"
            :error="errors.register_court"
            :hint="__('settings.company_information.form.register_court_hint')"
        >
            <DInput
                id="register_court"
                v-model="form.register_court"
                :placeholder="__('settings.company_information.form.register_court')"
                :is-valid="isFormFieldValid('register_court')"
                maxlength="45"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.register_number')"
            :is-required="true"
            :error="errors.register_number"
            :hint="__('settings.company_information.form.register_number_hint')"
        >
            <DInput
                id="register_number"
                v-model="form.register_number"
                :placeholder="__('settings.company_information.form.register_number')"
                :is-valid="isFormFieldValid('register_number')"
                maxlength="45"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.vat_number')"
            :is-required="true"
            :error="errors.vat_number"
            :hint="__('settings.company_information.form.vat_number_hint')"
        >
            <DInput
                id="vat_number"
                v-model="form.vat_number"
                :placeholder="__('settings.company_information.form.vat_number')"
                :is-valid="isFormFieldValid('vat_number')"
                maxlength="45"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.managing_directors')"
            :is-required="true"
            :error="errors.managing_directors"
            :hint="__('settings.company_information.form.managing_directors_hint')"
        >
            <DInput
                id="managing_directors"
                v-model="form.managing_directors"
                :placeholder="__('settings.company_information.form.managing_directors')"
                :is-valid="isFormFieldValid('managing_directors')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.company_information.form.ch_trustee')"
            :is-required="true"
            :error="errors.ch_trustee"
            :hint="__('settings.company_information.form.ch_trustee_hint')"
        >
            <DFormCheck
                id="ch_trustee"
                v-model="form.ch_trustee"
                inline
                :is-valid="isFormFieldValid('ch_trustee')"
            />
        </DFormRow>
        <DFormRow
            v-if="form.ch_trustee"
            :label="__('settings.company_information.form.ch_trustee_name')"
            :is-required="true"
            :error="errors.ch_trustee_name"
            :hint="__('settings.company_information.form.ch_trustee_name_hint')"
        >
            <DInput
                id="ch_trustee_name"
                v-model="form.ch_trustee_name"
                :placeholder="__('settings.company_information.form.ch_trustee_name')"
                :is-valid="isFormFieldValid('ch_trustee_name')"
                maxlength="45"
            />
        </DFormRow>
        <DFormRow
            v-if="form.ch_trustee"
            :label="__('settings.company_information.form.ch_trustee_address')"
            :is-required="true"
            :error="errors.ch_trustee_address"
            :hint="__('settings.company_information.form.ch_trustee_address_hint')"
        >
            <DInput
                id="ch_trustee_address"
                v-model="form.ch_trustee_address"
                :placeholder="__('settings.company_information.form.ch_trustee_address')"
                :is-valid="isFormFieldValid('ch_trustee_address')"
                maxlength="45"
            />
        </DFormRow>
        <DFormRow
            v-if="form.ch_trustee"
            :label="__('settings.company_information.form.ch_trustee_post_code')"
            :is-required="true"
            :error="errors.ch_trustee_post_code"
            :hint="__('settings.company_information.form.ch_trustee_post_code_hint')"
        >
            <DInput
                id="ch_trustee_post_code"
                v-model="form.ch_trustee_post_code"
                :placeholder="__('settings.company_information.form.ch_trustee_post_code')"
                :is-valid="isFormFieldValid('ch_trustee_post_code')"
                maxlength="45"
            />
        </DFormRow>
        <DFormRow
            v-if="form.ch_trustee"
            :label="__('settings.company_information.form.ch_trustee_city')"
            :is-required="true"
            :error="errors.ch_trustee_city"
            :hint="__('settings.company_information.form.ch_trustee_city_hint')"
        >
            <DInput
                id="ch_trustee_city"
                v-model="form.ch_trustee_city"
                :placeholder="__('settings.company_information.form.ch_trustee_city')"
                :is-valid="isFormFieldValid('ch_trustee_city')"
                maxlength="45"
            />
        </DFormRow>
        <DFormRow
            v-if="form.ch_trustee"
            :label="__('settings.company_information.form.ch_trustee_vat_number')"
            :is-required="true"
            :error="errors.ch_vat_id_number"
            :hint="__('settings.company_information.form.ch_trustee_vat_number_hint')"
        >
            <DInput
                id="ch_vat_id_number"
                v-model="form.ch_vat_id_number"
                :placeholder="__('settings.company_information.form.ch_trustee_vat_number')"
                :is-valid="isFormFieldValid('ch_vat_id_number')"
                maxlength="45"
            />
        </DFormRow>
        <DFormControls
            :dirty="form.isDirty"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>
export default {
    name: 'CompanyInformationForm',
    props: {
        countries: {
            type: Array,
            default: () => { return [] }
        },
        companyInformation: {
            type: Object,
            default: () => { return {} }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        }
    },
    emits: ['form-submitted'],
    data () {
        return {
            form: this.$inertia.form({
                logo: null,
                logo_base64: null,
                logo_dark: null,
                logo_dark_base64: null,
                company_name: this.companyInformation?.company_name ?? null,
                address: this.companyInformation?.address ?? null,
                post_code: this.companyInformation?.post_code ?? null,
                city: this.companyInformation?.city ?? null,
                country_code: this.companyInformation?.country_code ?? this.$page.props.defaultCountry,
                default_country_code: this.companyInformation?.default_country_code ?? this.$page.props.defaultCountry,
                register_court: this.companyInformation?.register_court ?? null,
                register_number: this.companyInformation?.register_number ?? null,
                vat_number: this.companyInformation?.vat_number ?? null,
                managing_directors: this.companyInformation?.managing_directors ?? null,
                ch_trustee: this.companyInformation?.ch_trustee ?? false,
                ch_trustee_name: this.companyInformation?.ch_trustee_name ?? null,
                ch_trustee_address: this.companyInformation?.ch_trustee_address ?? null,
                ch_trustee_post_code: this.companyInformation?.ch_trustee_post_code ?? null,
                ch_trustee_city: this.companyInformation?.ch_trustee_city ?? null,
                ch_vat_id_number: this.companyInformation?.ch_vat_id_number ?? null
            }),
            logoSrcContents: this.companyInformation.logo !== undefined ? `data:image/svg+xml;base64,${this.companyInformation.logo}` : null,
            logoDarkSrcContents: this.companyInformation.logo_dark ? `data:image/svg+xml;base64,${this.companyInformation.logo_dark}` : null
        }
    },
    methods: {
        submitForm () {
            this.$emit('form-submitted', this.form)
        },
        addLogo (FileList) {
            if (FileList.length === 0) {
                return
            }

            this.form.logo = FileList[0]
            this.logoSrcContents = URL.createObjectURL(FileList[0])
        },
        addLogoDark (FileList) {
            if (FileList.length === 0) {
                return
            }

            this.form.logo_dark = FileList[0]
            this.logoDarkSrcContents = URL.createObjectURL(FileList[0])
        }
    }
}
</script>
<style scoped>
.company__image {
    max-width: 150px;
}
.company__image--dark {
    background-color: #1E293B;
    padding: 10px;
    border-radius: 8px;
}
</style>
